/** @file Scanline geometry of a PNG: one pass, or Adam7's seven, each with its exact byte count. */

/** A non-empty pass: its first pixel and step in the image, its size, and its row bytes without the filter byte. */
export interface PngPass {
  x0: number;
  y0: number;
  dx: number;
  dy: number;
  width: number;
  height: number;
  rowBytes: number;
}

/** Adam7 passes as [x0, y0, dx, dy] (PNG specification, interlace method 1). */
const ADAM7 = [[0, 0, 8, 8], [4, 0, 8, 8], [0, 4, 4, 8], [2, 0, 4, 4], [0, 2, 2, 4], [1, 0, 2, 2], [0, 1, 1, 2]] as const;
const PLAIN = [[0, 0, 1, 1]] as const;

/** The non-empty passes of a `width` x `height` image; its inflated data is Σ height × (1 + rowBytes) bytes. */
export function pngPasses(width: number, height: number, bitsPerPixel: number, interlaced: boolean): PngPass[] {
  return (interlaced ? ADAM7 : PLAIN)
    .map(([x0, y0, dx, dy]) => {
      const w = Math.max(0, Math.ceil((width - x0) / dx));
      const h = Math.max(0, Math.ceil((height - y0) / dy));
      return { x0, y0, dx, dy, width: w, height: h, rowBytes: Math.ceil((w * bitsPerPixel) / 8) };
    })
    .filter((pass) => pass.width > 0 && pass.height > 0);
}
